import { useState, useEffect, useCallback } from 'react';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useGenerateCme, useBulkCreatePreventivi } from '../../hooks';
import { formatCurrency } from '../../utils/pricing';
import type { CmeLineItem, CmeGenerateInput } from '../../types';

interface ComputoMetricoWizardProps {
  onClose: () => void;
  lavoroId?: string;
  lavoroNome?: string;
  lavoroDescrizione?: string;
}

interface EditableItem extends CmeLineItem {
  selected: boolean;
}

const STEPS = ['Descrizione', 'Generazione', 'Revisione'] as const;

export function ComputoMetricoWizard({ onClose, lavoroId, lavoroNome, lavoroDescrizione }: ComputoMetricoWizardProps) {
  const { currentProject } = useProjectContext();
  const generateMutation = useGenerateCme();
  const bulkSaveMutation = useBulkCreatePreventivi();

  const [step, setStep] = useState(0);
  const [editableItems, setEditableItems] = useState<EditableItem[]>([]);

  const immobile = currentProject?.immobile;

  const [formData, setFormData] = useState<{
    descrizione: string;
    superficie: string;
    tipoImmobile: string;
    annoCostruzione: string;
    classeEnergetica: string;
    qualita: 'Economica' | 'Standard' | 'Premium';
    esecuzione: 'Esterni' | 'In proprio';
  }>({
    descrizione: [lavoroNome, lavoroDescrizione].filter(Boolean).join('\n'),
    superficie: immobile?.superficie || '',
    tipoImmobile: immobile?.tipoImmobile || '',
    annoCostruzione: immobile?.annoCostruzione?.toString() || '',
    classeEnergetica: immobile?.classeEnergetica || '',
    qualita: 'Standard',
    esecuzione: 'Esterni',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Step 1 → Step 2: fire AI generation
  const handleGenerate = useCallback(() => {
    if (!currentProject) return;

    const input: CmeGenerateInput = {
      projectId: currentProject.id,
      lavoroId,
      descrizione: formData.descrizione,
      ...(formData.superficie && { superficie: formData.superficie }),
      ...(formData.tipoImmobile && { tipoImmobile: formData.tipoImmobile }),
      ...(formData.annoCostruzione && {
        annoCostruzione: parseInt(formData.annoCostruzione, 10),
      }),
      ...(formData.classeEnergetica && { classeEnergetica: formData.classeEnergetica }),
      qualita: formData.qualita,
      esecuzione: formData.esecuzione,
    };

    generateMutation.mutate(input, {
      onSuccess: (result) => {
        setEditableItems(
          result.items.map((item) => ({ ...item, selected: true }))
        );
        setStep(2);
      },
    });
  }, [currentProject, formData, generateMutation]);

  // Auto-fire generation when entering step 1
  useEffect(() => {
    if (step === 1 && !generateMutation.isPending && !generateMutation.isSuccess) {
      handleGenerate();
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleItem = (index: number) => {
    setEditableItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const toggleAll = () => {
    const allSelected = editableItems.every((i) => i.selected);
    setEditableItems((prev) =>
      prev.map((item) => ({ ...item, selected: !allSelected }))
    );
  };

  const updateItemCost = (
    index: number,
    field: 'costoMin' | 'costoMed' | 'costoMax',
    value: string
  ) => {
    setEditableItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: parseFloat(value) || 0 } : item
      )
    );
  };

  // Recalculate totals from selected items
  const selectedTotals = editableItems
    .filter((i) => i.selected)
    .reduce(
      (acc, item) => ({
        totaleMin: acc.totaleMin + item.costoMin,
        totaleMed: acc.totaleMed + item.costoMed,
        totaleMax: acc.totaleMax + item.costoMax,
      }),
      { totaleMin: 0, totaleMed: 0, totaleMax: 0 }
    );

  const selectedCount = editableItems.filter((i) => i.selected).length;

  const handleSave = () => {
    if (!currentProject) return;

    const items = editableItems
      .filter((i) => i.selected)
      .map((item) => ({
        categoria: item.categoria,
        voce: item.voce,
        costoMin: item.costoMin.toFixed(2),
        costoMed: item.costoMed.toFixed(2),
        costoMax: item.costoMax.toFixed(2),
        note: item.note || undefined,
      }));

    bulkSaveMutation.mutate(
      { projectId: currentProject.id, lavoroId, items },
      { onSuccess: () => onClose() }
    );
  };

  const canProceedStep0 = formData.descrizione.trim().length >= 10;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto animate-slide-up">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Stima AI - Computo Metrico
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={generateMutation.isPending}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-6">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
                    i === step
                      ? 'bg-primary-600 text-white'
                      : i < step
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {i < step ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 ${i < step ? 'bg-primary-300' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mb-4">{STEPS[step]}</p>

          {/* Step 0: Descrizione */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="input-group">
                <label htmlFor="descrizione" className="input-label">
                  Descrizione dei lavori *
                </label>
                <textarea
                  id="descrizione"
                  name="descrizione"
                  value={formData.descrizione}
                  onChange={handleChange}
                  className="w-full"
                  rows={4}
                  placeholder="es. Ristrutturazione completa bagno 8mq: demolizione pavimento e rivestimento, nuovo impianto idraulico, sanitari, box doccia, piastrelle..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimo 10 caratteri. Piu dettagli fornisci, migliore sara la stima.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="input-group">
                  <label htmlFor="qualita" className="input-label">
                    Qualità lavori
                  </label>
                  <select
                    id="qualita"
                    name="qualita"
                    value={formData.qualita}
                    onChange={handleChange}
                    className="w-full"
                  >
                    <option value="Economica">Economica</option>
                    <option value="Standard">Standard</option>
                    <option value="Premium">Premium</option>
                  </select>
                </div>
                <div className="input-group">
                  <label htmlFor="esecuzione" className="input-label">
                    Esecuzione
                  </label>
                  <select
                    id="esecuzione"
                    name="esecuzione"
                    value={formData.esecuzione}
                    onChange={handleChange}
                    className="w-full"
                  >
                    <option value="Esterni">Esterni</option>
                    <option value="In proprio">In proprio</option>
                  </select>
                </div>
              </div>

              {/* Show immobile data summary if available */}
              {immobile && (immobile.superficie || immobile.tipoImmobile || immobile.annoCostruzione || immobile.classeEnergetica || immobile.indirizzo) && (
                <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
                  <span className="font-medium text-gray-600">Dati immobile (auto):</span>
                  {' '}
                  {[
                    immobile.indirizzo,
                    immobile.superficie && `${immobile.superficie} mq`,
                    immobile.tipoImmobile,
                    immobile.annoCostruzione && `anno ${immobile.annoCostruzione}`,
                    immobile.classeEnergetica && `classe ${immobile.classeEnergetica}`,
                  ].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          )}

          {/* Step 1: Generazione (loading) */}
          {step === 1 && (
            <div className="flex flex-col items-center justify-center py-12">
              {generateMutation.isPending && (
                <>
                  <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4" />
                  <p className="text-gray-700 font-medium">Generazione in corso...</p>
                  <p className="text-sm text-gray-500 mt-1">
                    L'AI sta analizzando i lavori e stimando i costi
                  </p>
                </>
              )}
              {generateMutation.isError && (
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-danger-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-medium">Errore nella generazione</p>
                  <p className="text-sm text-danger-600 mt-1">
                    {generateMutation.error?.message || 'Errore sconosciuto'}
                  </p>
                  <button
                    onClick={() => {
                      generateMutation.reset();
                      setStep(0);
                    }}
                    className="btn-secondary mt-4"
                  >
                    Torna indietro
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Revisione */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Totals summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    Riepilogo ({selectedCount}/{editableItems.length} voci selezionate)
                  </h4>
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {editableItems.every((i) => i.selected)
                      ? 'Deseleziona tutto'
                      : 'Seleziona tutto'}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500">Min</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(selectedTotals.totaleMin)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Medio</p>
                    <p className="text-sm font-semibold text-primary-700">
                      {formatCurrency(selectedTotals.totaleMed)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Max</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(selectedTotals.totaleMax)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Items list */}
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {editableItems.map((item, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-3 transition-colors ${
                      item.selected
                        ? 'border-primary-200 bg-white'
                        : 'border-gray-200 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => toggleItem(index)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                            {item.categoria}
                          </span>
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {item.voce}
                          </span>
                        </div>
                        {item.note && (
                          <p className="text-xs text-gray-500 mb-2">{item.note}</p>
                        )}
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-xs text-gray-400">Min</label>
                            <input
                              type="number"
                              value={item.costoMin}
                              onChange={(e) =>
                                updateItemCost(index, 'costoMin', e.target.value)
                              }
                              className="w-full text-xs py-1"
                              min="0"
                              step="0.01"
                              disabled={!item.selected}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400">Med</label>
                            <input
                              type="number"
                              value={item.costoMed}
                              onChange={(e) =>
                                updateItemCost(index, 'costoMed', e.target.value)
                              }
                              className="w-full text-xs py-1"
                              min="0"
                              step="0.01"
                              disabled={!item.selected}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-400">Max</label>
                            <input
                              type="number"
                              value={item.costoMax}
                              onChange={(e) =>
                                updateItemCost(index, 'costoMax', e.target.value)
                              }
                              className="w-full text-xs py-1"
                              min="0"
                              step="0.01"
                              disabled={!item.selected}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 pt-6">
            {step === 0 && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary flex-1"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="btn-primary flex-1"
                  disabled={!canProceedStep0}
                >
                  Genera Stima
                </button>
              </>
            )}
            {step === 2 && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    generateMutation.reset();
                    setStep(0);
                  }}
                  className="btn-secondary flex-1"
                >
                  Rigenera
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="btn-primary flex-1"
                  disabled={selectedCount === 0 || bulkSaveMutation.isPending}
                >
                  {bulkSaveMutation.isPending
                    ? 'Salvataggio...'
                    : `Salva ${selectedCount} Voci`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
